import { WelcomeResponse } from '@aaa-ncnu-ie/mypolicy-api-customers';
import { GetClubsByZoneResponse } from '@aaa-ncnu-ie/mypolicy-api-registrations';
import { capitalize } from '@mui/material';
import { add, Duration } from 'date-fns';

export const preventNonNumbers = (
  value: string = '',
  setValue: (val: string) => void
) => {
  const regex = /^[0-9]*$/;

  if (regex.test(value)) {
    setValue(value);
  }
};

export const maskNumbers = (
  value: string = '',
  setValue: (val: string) => void
) => {
  let formattedValue = '';
  for (let i = 0; i < value.length - 4; i += 1) {
    formattedValue += String.fromCharCode(8226);
  }
  formattedValue += value.substring(value.length - 4);

  setValue(formattedValue);
};

export const setCookieWithExpiration = (
  cookieName: string,
  value: string,
  expiration: Duration,
  path: string = '/'
): void => {
  const expirationDate = add(new Date(), expiration);

  document.cookie = `${cookieName}=${value}; expires=${expirationDate.toUTCString()}; path=${path}`;
};

export const getCookie = (cookieName: string) => {
  const cookies = document.cookie.split('; ');

  const found = cookies.find((cookie) => cookie.split('=')?.[0] === cookieName);

  return found?.substring(found?.indexOf('=') + 1);
};

export const deleteCookie = (cookieName: string): void => {
  document.cookie = `${cookieName}=; Max-Age=0; path=/; domain=${location.hostname}`;
};

export const setWithExpiry = (
  key: string,
  value: string | boolean | number,
  ttl: number // # of days until expiry
): void => {
  const now = new Date();

  const daysToMs = ttl * (1000 * 60 * 60 * 24);

  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value,
    expiry: now.getTime() + daysToMs,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

export const getWithExpiry = (key: string): boolean | null => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr);
  const now = new Date();

  if (now.getTime() > item.expiry) {
    // remove if expired
    localStorage.removeItem(key);
    return null;
  }

  return item.value;
};

export const sentenceCase = (text: string): string =>
  capitalize(text.toLowerCase());

export const capitalizeName = (name: string = ''): string => {
  return (
    name
      .toLowerCase()
      // @ts-expect-error - Unicode property escapes
      .replace(/(^|\P{L})\p{L}/gu, (match) => match.toUpperCase())
  );
};

export const loadScript = (src: string) =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = src;
    script.id = src;
    script.onload = () => resolve(true);
    script.onerror = reject;

    document.body.appendChild(script);
  });

export const loadLink = (href: string) =>
  new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.as = 'style';
    link.type = 'text/css';
    link.href = href;
    link.id = href;
    link.onload = () => resolve(true);
    link.onerror = reject;

    document.head.appendChild(link);
  });

export const removeElement = (id: string) => {
  const element = document.getElementById(id);
  element?.remove();
};

export const getCustomerName = (data?: WelcomeResponse) => {
  if (data && data?.userInfo?.loginType === 'cloud') {
    return { firstName: data?.firstName, lastName: data?.lastName };
  } else {
    return {
      firstName: data?.userInfo?.firstName || data?.firstName,
      lastName: data?.userInfo?.lastName || data?.lastName,
    };
  }
};

export const editAccessibilityButtonStyles = () => {
  const shadow = document.getElementById('un-seal');
  const root = shadow?.shadowRoot;
  const btn = root?.getElementById('un-s-open');
  btn?.setAttribute(
    'style',
    'background: #115CD4; outline: solid 4px #115CD4; box-shadow: none'
  );
  if (btn) btn.innerHTML = 'About accessibility status';
};

export const getLogoutUrl = (urlFromWelcomeApi: string) => {
  if (window.location.hostname.includes('localhost')) {
    const [baseLogoutUrl] = urlFromWelcomeApi.split(
      '&post_logout_redirect_uri'
    );
    return `${baseLogoutUrl}&post_logout_redirect_uri=${window.location.origin}/logout`;
  }

  return urlFromWelcomeApi;
};

export const deleteUnnecessaryCookies = () => {
  const expiresString = 'expires=Thu, 01 Jan 1970 00:00:00 UTC;';
  const aaaDomainString = 'domain=aaa.com;';
  const simplePath = `path=/;`;

  const commonCookieDelete = `${expiresString}${aaaDomainString}${simplePath}`;
  document.cookie = `memberinfo=;${commonCookieDelete}`;
  document.cookie = `wisepops_session=;${commonCookieDelete}`;
  document.cookie = `wisepops=;${commonCookieDelete}`;
  document.cookie = `wisepops_visits=;${commonCookieDelete}`;
  document.cookie = `wisepops_props=;${commonCookieDelete}`;
  document.cookie = `MGX_UC=;${commonCookieDelete}`;
};

export const checkForZipCodeCookieAndRedirect = () => {
  const mprZipCookie = getCookie('mpr_zip');
  const zipCodeCookie: GetClubsByZoneResponse | null = mprZipCookie
    ? JSON.parse(mprZipCookie)
    : null;

  if (
    zipCodeCookie &&
    zipCodeCookie.loginType === 'CLUB' &&
    zipCodeCookie.loginURL
  ) {
    window.location.replace(zipCodeCookie.loginURL!);
    return true;
  }

  return false; // return booleans to help control loading state since window.location.replace has noticeable lag
};

/**
 * This is useful for implementing previous route links across the app.
 * Since we don't want to be linking directly to nested flows we need to make sure
 * we are grabbing the first path segment from complex paths and just flat out
 * redirecting for certain cases (/payments* -> /bills, etc...)
 */
export const getSmartPath = (previousPath: string) => {
  const firstPathSegment = previousPath.match(/^\/[^/]+/);
  if (firstPathSegment && firstPathSegment?.length > 0) {
    switch (firstPathSegment[0]) {
      case '/multiple-payment/':
      case '/payment':
        return '/bills'; // we don't have any reason to send them anywhere but the bills page
      case '/edit':
        return '/policies';
      case '/wallet':
      case '/autopay':
      case '/claims':
        return firstPathSegment[0];
      default:
        return previousPath;
    }
  }
  return '/';
};

export const getClubCode = (userInfoClubCode: string | undefined): string => {
  const zipCookieInfo = getCookie('mpr_zip');
  const clubCodeInfo = zipCookieInfo ? JSON.parse(zipCookieInfo) : {};
  return userInfoClubCode || clubCodeInfo.clubCode;
};

export const renameFile = (originalFile: File, newName: string) => {
  return new File([originalFile], newName, {
    type: originalFile.type,
    lastModified: originalFile.lastModified,
  });
};

export const truncateMiddleOfLongFileNames = (fileName: string) => {
  if (fileName.length < 40) {
    return fileName;
  } else {
    const fileExtensionIndex = fileName.lastIndexOf('.');
    return `${fileName.substring(0, 25)}...${fileName.substring(fileExtensionIndex - 3)}`;
  }
};

export const truncateFraudMessage = (
  text: string = '',
  maxChars: number = 140
): string => {
  const normalized = text.trim();
  if (!normalized) return '';
  if (normalized.length <= maxChars) return '';

  const slice = normalized.slice(0, maxChars);
  const lastSpaceIdx = slice.lastIndexOf(' ');
  const preview = lastSpaceIdx > 0 ? slice.slice(0, lastSpaceIdx) : slice;
  return `${preview.trim()}...`;
};
